import * as React from "react";
import { useEffect, useState } from "react";
import { Page } from "@/interfaces";
import { Pagination } from "semantic-ui-react";
import { SemanticWIDTHS } from "semantic-ui-react/dist/commonjs/generic";
import { exportDataToCsv, useDebounce } from "@/utils";
import { Button, Input, Table, Spinner } from "@/components/ui";
import { Search } from "lucide-react";

export interface UserTableHeader {
  title: string;
  width?: SemanticWIDTHS;
}

export interface UserTableSearchQuery {
  searchTerm: string;
  page: number;
  size: number;
}

interface UserTableProps<T> {
  headers: UserTableHeader[];
  queryFunction: (query: UserTableSearchQuery) => Promise<Page<T>>;
  exportMapper?: (item: T) => {};
  renderRow: (item: T) => React.ReactNode;
  hideSearch?: boolean;
}

const UserTable: React.FC<UserTableProps<any>> = <T,>({
  headers,
  queryFunction,
  exportMapper,
  renderRow,
  hideSearch,
}: UserTableProps<T>) => {
  const [data, setData] = useState<T[]>([]);
  const [totalPages, setTotalPages] = React.useState<number>(0);
  const [pageNumber, setPageNumber] = React.useState<number>(0);
  const [searchTerm, setSearchTerm] = React.useState<string>("");
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [exportLoading, setExportLoading] = React.useState<boolean>(false);

  const getMoreData = () => {
    setIsLoading(true);
    queryFunction({ page: pageNumber, size: 20, searchTerm })
      .then(newData => {
        setData(newData.content);
        setTotalPages(newData.totalPages || 0);
        setIsLoading(false);
      })
      .catch(console.error);
  };

  const onNewSearchTerm = useDebounce(() => {
    getMoreData();
  }, 300);

  useEffect(() => {
    onNewSearchTerm();
  }, [searchTerm]);

  useEffect(() => {
    getMoreData();
  }, [pageNumber]);

  const exportData = async () => {
    setExportLoading(true);
    let page = 0;
    let last = false;
    let allData: T[] = [];
    while (!last) {
      const response = await queryFunction({ page, searchTerm, size: 100 });
      last = response.last || false;
      allData = allData.concat(response.content);
      page++;
    }

    const exportData = allData.map(i => exportMapper!(i));

    exportDataToCsv("SocialCoachData", exportData);
    setExportLoading(false);
  };

  return (
    <div className="w-full flex flex-col gap-5">
      {/* Top Filter & Actions Header */}
      {(!hideSearch || !!exportMapper) && (
        <div className="flex flex-col md:flex-row items-center gap-4 w-full">
          {/* Search Bar */}
          {!hideSearch && (
            <div className="relative w-full md:w-80 shrink-0">
              {isLoading ? (
                <Spinner className="absolute right-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground animate-spin" />
              ) : (
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              )}
              <Input
                value={searchTerm}
                placeholder="Search by Name or Email..."
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-9 h-10 w-full text-xs font-semibold bg-white border-gray-200"
              />
            </div>
          )}

          <div className="grow" />

          {/* Export Action Button */}
          {!!exportMapper && (
            <div className="w-full md:w-auto">
              <Button
                variant="outline"
                disabled={exportLoading}
                onClick={exportData}
                className="h-10 font-bold border-gray-200 cursor-pointer w-full md:w-auto"
              >
                {exportLoading && <Spinner className="mr-2 size-3.5" />}
                Export List
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Main Table view */}
      <div className="w-full border border-slate-200/60 rounded-xl overflow-hidden bg-background shadow-xs">
        <Table className="w-full text-sm">
          <Table.Header className="bg-slate-50/50 border-b border-slate-100">
            <Table.Row className="hover:bg-transparent">
              {headers.map(header => (
                <Table.Head
                  key={`user-table-header-${header.title}`}
                  className="font-bold text-slate-500 uppercase tracking-wider text-[11px] py-3 first:border-l-0"
                >
                  {header.title}
                </Table.Head>
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {!isLoading &&
              data.map((item, index) => (
                <Table.Row
                  key={`user-table-row-${index}`}
                  className="hover:bg-slate-50/40 border-b border-slate-100 transition-colors duration-150"
                >
                  {renderRow(item)}
                </Table.Row>
              ))}
          </Table.Body>
        </Table>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center py-4">
          <Pagination
            defaultActivePage={1}
            boundaryRange={3}
            pointing
            secondary
            totalPages={totalPages}
            onPageChange={(_, data) => {
              setPageNumber(Number(data.activePage) - 1);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default UserTable;
